import React, { useRef, useState, useCallback } from 'react';
import { Upload, X, FileText, FileImage, File } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface FilePreview {
  id: string;
  file: File;
  preview?: string;
  type: 'image' | 'pdf' | 'other';
}

interface UnifiedFileUploadProps {
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in MB
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
  formatHint?: string;
  children?: React.ReactNode;
  existingFiles?: FilePreview[];
  onRemoveFile?: (fileId: string) => void;
}

export default function UnifiedFileUpload({
  accept = "image/*",
  multiple = false,
  maxFiles = 1,
  maxSize = 20,
  onFilesChange,
  disabled = false,
  className,
  formatHint,
  children,
  existingFiles = [],
  onRemoveFile
}: UnifiedFileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previews, setPreviews] = useState<FilePreview[]>([]);

  const createFilePreview = useCallback(async (file: File): Promise<FilePreview> => {
    const id = Math.random().toString(36).substr(2, 9);
    const type = file.type.startsWith('image/') ? 'image' : 
                 file.type === 'application/pdf' ? 'pdf' : 'other';
    
    let preview = undefined;
    if (type === 'image') {
      preview = URL.createObjectURL(file);
    }

    return { id, file, preview, type };
  }, []);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const currentCount = previews.length + existingFiles.length;
    
    // Check file count limit
    if (currentCount + fileArray.length > maxFiles) {
      console.warn(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate and create previews
    const validFiles: File[] = [];
    const newPreviews: FilePreview[] = [];

    for (const file of fileArray) {
      // Validate file size
      if (file.size > maxSize * 1024 * 1024) {
        console.warn(`File ${file.name} exceeds ${maxSize}MB limit`);
        continue;
      }

      validFiles.push(file);
      const preview = await createFilePreview(file);
      newPreviews.push(preview);
    }

    if (validFiles.length > 0) {
      const updatedPreviews = multiple ? [...previews, ...newPreviews] : newPreviews;
      setPreviews(updatedPreviews);
      onFilesChange(multiple ? [...previews.map(p => p.file), ...validFiles] : validFiles);
    }
  }, [previews, existingFiles, maxFiles, maxSize, multiple, onFilesChange, createFilePreview]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const removeFile = (fileId: string) => {
    const updatedPreviews = previews.filter(p => p.id !== fileId);
    setPreviews(updatedPreviews);
    onFilesChange(updatedPreviews.map(p => p.file));
    
    // Clean up preview URLs
    const removedPreview = previews.find(p => p.id === fileId);
    if (removedPreview?.preview) {
      URL.revokeObjectURL(removedPreview.preview);
    }
  };

  const getFileIcon = (type: string) => {
    if (type === 'image') return <FileImage className="h-4 w-4" />;
    if (type === 'pdf') return <FileText className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const currentCount = previews.length + existingFiles.length;
  const canUpload = currentCount < maxFiles && !disabled;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Upload Area */}
      {canUpload && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={cn(
            "relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
            dragActive ? "border-primary bg-primary/10 border-solid" : "border-border hover:border-primary/50",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleInputChange}
            disabled={disabled}
            className="hidden"
          />
          
          <Upload className={cn("mx-auto h-8 w-8 mb-2", dragActive ? "text-primary" : "text-muted-foreground")} />
          <div className={cn("text-sm font-medium mb-1", dragActive ? "text-primary" : "text-foreground")}>
            {dragActive ? "Drop files here" : (children || `Click to upload ${multiple ? 'files' : 'file'} or drag and drop`)}
          </div>
          {formatHint && (
            <p className="text-xs text-muted-foreground">
              {formatHint}
            </p>
          )}
          {multiple && (
            <p className="text-xs text-muted-foreground mt-1">
              {currentCount}/{maxFiles} files uploaded
            </p>
          )}
        </div>
      )}

      {/* File Previews */}
      {(previews.length > 0 || existingFiles.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Existing Files */}
          {existingFiles.map((file) => (
            <div key={file.id} className="relative border border-border rounded-lg overflow-hidden">
              {file.type === 'image' && file.preview ? (
                <img
                  src={file.preview}
                  alt="File preview"
                  className="w-full h-24 object-cover"
                />
              ) : (
                <div className="w-full h-24 bg-muted flex items-center justify-center">
                  {getFileIcon(file.type)}
                  <span className="ml-2 text-sm text-muted-foreground truncate">
                    {file.file.name}
                  </span>
                </div>
              )}
              {onRemoveFile && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1 h-6 w-6 p-0"
                  onClick={() => onRemoveFile(file.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}

          {/* New File Previews */}
          {previews.map((preview) => (
            <div key={preview.id} className="relative border border-border rounded-lg overflow-hidden">
              {preview.type === 'image' && preview.preview ? (
                <img
                  src={preview.preview}
                  alt="File preview"
                  className="w-full h-24 object-cover"
                />
              ) : (
                <div className="w-full h-24 bg-muted flex items-center justify-center">
                  {getFileIcon(preview.type)}
                  <span className="ml-2 text-sm text-muted-foreground truncate max-w-20">
                    {preview.file.name}
                  </span>
                </div>
              )}
              <Button
                variant="destructive"
                size="sm"
                className="absolute top-1 right-1 h-6 w-6 p-0"
                onClick={() => removeFile(preview.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
